import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Material categories matching PlotterCuts material types
const materialCategories = [
  { value: 'carbon_fiber', label: 'Fibra de Carbono' },
  { value: 'fiberglass', label: 'Fibra de Vidrio' },
  { value: 'kevlar', label: 'Kevlar' },
  { value: 'hybrid', label: 'Híbrido' },
  { value: 'prepreg', label: 'Prepreg' },
  { value: 'dry_fiber', label: 'Fibra Seca' },
  { value: 'other', label: 'Otro' },
];
import { api } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { materialInventoryFormSchema, type MaterialInventoryFormData } from '@/schemas/materialInventorySchemas';
import type { MaterialInventory, MaterialLot } from '@/types/legacy';
import { Loader2, ScanBarcode, AlertCircle, Plus, Trash2, Clock, Snowflake } from 'lucide-react';
import { BarcodeScannerDialog } from '@/components/Barcode';
import { BarcodeInput } from '@/components/Barcode';
import type { BarcodeScanResult } from '@/hooks/useBarcodeScanner';

interface MaterialInventoryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  material?: MaterialInventory | null;
  onSuccess?: () => void;
  onDelete?: (material: MaterialInventory) => void;
}

interface MaterialLotDraft {
  id?: string;
  batch_number: string;
  roll_number: string;
  quantity: number;
  square_meters: number;
  production_date?: string;
  expiry_date?: string;
  notes?: string;
  is_active: boolean;
  is_in_use: boolean;
  removed_from_freezer_at?: string;
  is_currently_out?: boolean;
  thaw_count?: number;
}
export function MaterialInventoryFormDialog({ open, onOpenChange, material, onSuccess, onDelete }: MaterialInventoryFormDialogProps) {
  const { toast } = useToast();
  const { userRole } = useAuth();
  const queryClient = useQueryClient();
  const canSeeCosts = ['admin', 'supervisor', 'plotter'].includes(userRole || '');
  const [loading, setLoading] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [lots, setLots] = useState<MaterialLotDraft[]>([]);
  const [initialLotIds, setInitialLotIds] = useState<string[]>([]);

  const addLot = () => {
    setLots((prev) => [...prev, {
      batch_number: '',
      roll_number: '',
      quantity: 0,
      square_meters: 0,
      production_date: '',
      expiry_date: '',
      notes: '',
      is_active: true,
      is_in_use: false,
      is_currently_out: false,
      thaw_count: 0,
    }]);
  };

  const updateLot = <K extends keyof MaterialLotDraft>(index: number, key: K, value: MaterialLotDraft[K]) => {
    setLots((prev) => prev.map((lot, i) => (i === index ? { ...lot, [key]: value } : lot)));
  };

  const removeLot = (index: number) => {
    setLots((prev) => prev.filter((_, i) => i !== index));
  };

  const form = useForm<MaterialInventoryFormData>({
    resolver: zodResolver(materialInventoryFormSchema),
    defaultValues: {
      name: material?.name || '',
      code: material?.code || '',
      category: material?.category || '',
      material_type: material?.material_type || '',
      unit: material?.unit || 'kg',
      current_stock: material?.current_stock || 0,
      min_stock: material?.min_stock || 0,
      max_stock: material?.max_stock,
      square_meters: material?.square_meters || 0,
      cost_per_unit: material?.cost_per_unit,
      location: material?.location || '',
      supplier: material?.supplier || '',
      batch_number: material?.batch_number || '',
      roll_number: material?.roll_number || '',
      roll_count: material?.roll_count ?? undefined,
      production_date: material?.production_date || '',
      expiry_date: material?.expiry_date || '',
      storage_location: material?.storage_location || '',
      storage_temperature: material?.storage_temperature || '',
      storage_humidity: material?.storage_humidity || '',
      safety_data_sheet_url: material?.safety_data_sheet_url || '',
      last_restock_date: material?.last_restock_date || '',
      notes: material?.notes || '',
      is_freezer_material: material?.is_freezer_material ?? false,
      max_out_time_minutes: material?.max_out_time_minutes ?? 120,
      is_active: material?.is_active ?? true,
    },
  });
  const isFreezerMaterial = form.watch('is_freezer_material');

  // Reset form and missing fields when dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        name: material?.name || '',
        code: material?.code || '',
        category: material?.category || '',
        material_type: material?.material_type || '',
        unit: material?.unit || 'kg',
        current_stock: material?.current_stock || 0,
        min_stock: material?.min_stock || 0,
        max_stock: material?.max_stock,
        square_meters: material?.square_meters || 0,
        cost_per_unit: material?.cost_per_unit,
        location: material?.location || '',
        supplier: material?.supplier || '',
        batch_number: material?.batch_number || '',
        roll_number: material?.roll_number || '',
        roll_count: material?.roll_count ?? undefined,
        production_date: material?.production_date || '',
        expiry_date: material?.expiry_date || '',
        storage_location: material?.storage_location || '',
        storage_temperature: material?.storage_temperature || '',
        storage_humidity: material?.storage_humidity || '',
        safety_data_sheet_url: material?.safety_data_sheet_url || '',
        last_restock_date: material?.last_restock_date || '',
        notes: material?.notes || '',
        is_freezer_material: material?.is_freezer_material ?? false,
      max_out_time_minutes: material?.max_out_time_minutes ?? 120,
        is_active: material?.is_active ?? true,
      });
    } else {
      setMissingFields([]);
    }
  }, [open, material, form]);

  useEffect(() => {
    const loadLots = async () => {
      if (!open) return;

      if (!material?.id) {
        setInitialLotIds([]);
        setLots([]); // Inicializar vacío, el usuario agregará lotes manualmente
        return;
      }

      const { data, error } = await api
        .from('material_lots')
        .select('*')
        .eq('material_id', material.id)
        .eq('is_active', true)
        .order('created_at');

      if (error) {
        toast({ title: 'Error cargando lotes', description: error.message, variant: 'destructive' });
        return;
      }

      const dbLots = (data || []) as MaterialLot[];
      setInitialLotIds(dbLots.map((lot) => lot.id));
      setLots(dbLots.map((lot) => ({
        id: lot.id,
        batch_number: lot.batch_number || '',
        roll_number: lot.roll_number || '',
        quantity: Number(lot.quantity || 0),
        square_meters: Number(lot.square_meters || lot.quantity || 0),
        production_date: lot.production_date || '',
        expiry_date: lot.expiry_date || '',
        notes: lot.notes || '',
        is_active: lot.is_active ?? true,
        is_in_use: lot.is_in_use ?? false,
        removed_from_freezer_at: lot.removed_from_freezer_at,
        is_currently_out: lot.is_currently_out ?? false,
        thaw_count: lot.thaw_count ?? 0,
      })));
    };

    loadLots();
  }, [open, material?.id, toast]); // Cargar lotes siempre, sin depender de isFreezerMaterial

  // Auto-update square_meters when lots change (for freezer materials with rolls)
  useEffect(() => {
    if (isFreezerMaterial && lots.length > 0) {
      const totalSquareMeters = lots.reduce((sum, lot) => sum + (lot.square_meters || 0), 0);
      form.setValue('square_meters', totalSquareMeters);
    }
  }, [lots, isFreezerMaterial, form]);

  // Handle barcode scan result
  const handleBarcodeScan = (data: {
    code: string;
    quantity: number;
    material?: MaterialInventory;
    source: 'material_inventory' | 'raw_materials' | null;
  }) => {
    // Set the code
    form.setValue('code', data.code);

    // If material found in inventory, fill the form
    if (data.material && data.source === 'material_inventory') {
      form.setValue('name', data.material.name);
      form.setValue('category', data.material.category || '');
      form.setValue('material_type', data.material.material_type || '');
      form.setValue('unit', data.material.unit);
      form.setValue('min_stock', data.material.min_stock);
      form.setValue('max_stock', data.material.max_stock);
      form.setValue('cost_per_unit', data.material.cost_per_unit);
      form.setValue('location', data.material.location || '');
      form.setValue('supplier', data.material.supplier || '');
      form.setValue('batch_number', data.material.batch_number || '');
      form.setValue('roll_number', data.material.roll_number || '');
      form.setValue('roll_count', data.material.roll_count ?? undefined);
      form.setValue('production_date', data.material.production_date || '');
      form.setValue('expiry_date', data.material.expiry_date || '');
      form.setValue('storage_location', data.material.storage_location || '');
      form.setValue('storage_temperature', data.material.storage_temperature || '');
      form.setValue('storage_humidity', data.material.storage_humidity || '');
      form.setValue('safety_data_sheet_url', data.material.safety_data_sheet_url || '');
      form.setValue('is_freezer_material', data.material.is_freezer_material ?? false);
      form.setValue('notes', data.material.notes || '');

      // If scanned quantity is provided, add to current stock
      if (data.quantity > 0) {
        form.setValue('current_stock', data.quantity);
      } else {
        form.setValue('current_stock', data.material.current_stock);
      }

      toast({
        title: 'Datos cargados',
        description: `Material "${data.material.name}" cargado desde el inventario`,
      });
    } else if (data.quantity > 0) {
      // Only quantity was scanned, set it as current stock
      form.setValue('current_stock', data.quantity);
    }

    // Check for missing required fields
    const values = form.getValues();
    const missing: string[] = [];
    if (!values.name) missing.push('Nombre');
    if (!values.unit) missing.push('Unidad');

    // Recommended fields
    const recommended: string[] = [];
    if (!values.supplier) recommended.push('Proveedor');
    if (!values.location) recommended.push('Ubicación');
    if (values.is_freezer_material) {
      if (!values.batch_number) recommended.push('Número de lote');
      if (!values.roll_number) recommended.push('Número de rollo');
      if (!values.roll_count) recommended.push('Cantidad de bobinas/rollos');
      if (!values.production_date) recommended.push('Fecha de producción');
      if (!values.expiry_date) recommended.push('Fecha de caducidad');
      if (!values.storage_location) recommended.push('Ubicación de almacenamiento');
      if (!values.storage_temperature) recommended.push('Temperatura de almacenamiento');
      if (!values.storage_humidity) recommended.push('Humedad de almacenamiento');
      if (!values.safety_data_sheet_url) recommended.push('URL de hoja de seguridad');
    }

    setMissingFields([...missing, ...recommended.map(f => `${f} (recomendado)`)]);
  };

  // Handle USB scanner input (hidden input)
  const handleUsbScan = (result: BarcodeScanResult) => {
    // For USB scanner, just set the code and open scanner dialog for lookup
    form.setValue('code', result.code);
    if (result.quantity > 0) {
      form.setValue('current_stock', result.quantity);
    }
    toast({
      title: 'Código escaneado',
      description: `Código: ${result.code}${result.quantity > 0 ? `, Cantidad: ${result.quantity}` : ''}`,
    });
  };

  const onSubmit = async (data: MaterialInventoryFormData) => {
    setLoading(true);
    try {
      // Procesar lotes para TODOS los materiales, no solo freezer
      const normalizedLots = lots
        .map((lot) => ({
          ...lot,
          batch_number: lot.batch_number?.trim() || '',
          roll_number: lot.roll_number?.trim() || '',
        }))
        .filter((lot) => lot.batch_number || lot.roll_number || lot.quantity > 0 || lot.square_meters > 0);

      const primaryLot = normalizedLots.find((lot) => lot.is_in_use) || normalizedLots[0];

      // Calculate totals from all lots (for freezer materials)
      const totalSquareMetersFromLots = normalizedLots.reduce(
        (sum, lot) => sum + (lot.square_meters || 0),
        0
      );
      const totalQuantityFromLots = normalizedLots.reduce(
        (sum, lot) => sum + (lot.quantity || 0),
        0
      );

      // Usar datos del lote primario para TODOS los materiales que tengan lotes
      const lotPayloadData = normalizedLots.length > 0
        ? {
            batch_number: primaryLot?.batch_number || null,
            roll_number: primaryLot?.roll_number || null,
            roll_count: normalizedLots.length || null,
            production_date: primaryLot?.production_date || null,
            expiry_date: primaryLot?.expiry_date || null,
          }
        : {
            batch_number: null,
            roll_number: null,
            roll_count: null,
            production_date: null,
            expiry_date: null,
          };

      const payload = {
        name: data.name,
        code: data.code || null,
        category: data.category || null,
        material_type: data.material_type || null,
        unit: data.unit,
        // Si hay lotes con cantidades, usar la suma de los lotes
        current_stock: totalQuantityFromLots > 0
          ? totalQuantityFromLots
          : data.current_stock,
        min_stock: data.min_stock,
        max_stock: data.max_stock || null,
        // Si hay lotes con m², usar la suma de los lotes
        square_meters: totalSquareMetersFromLots > 0
          ? totalSquareMetersFromLots
          : (data.square_meters || 0),
        cost_per_unit: data.cost_per_unit || null,
        location: data.location || null,
        supplier: data.supplier || null,
        last_restock_date: data.last_restock_date || null,
        storage_location: data.storage_location || null,
        storage_temperature: data.storage_temperature || null,
        storage_humidity: data.storage_humidity || null,
        safety_data_sheet_url: data.safety_data_sheet_url || null,
        is_freezer_material: data.is_freezer_material,
        max_out_time_minutes: data.is_freezer_material ? data.max_out_time_minutes : null,
        ...lotPayloadData,
        notes: data.notes || null,
        is_active: data.is_active,
      };

      let materialId = material?.id || null;

      if (material) {
        const { error } = await api
          .from('material_inventory')
          .update(payload)
          .eq('id', material.id);
        if (error) throw error;
      } else {
        const { data: created, error } = await api
          .from('material_inventory')
          .insert([payload])
          .select('id')
          .single();
        if (error) throw error;
        materialId = created.id;
      }

      if (!materialId) throw new Error('No se pudo resolver el material');

      // Guardar lotes para TODOS los materiales
      if (normalizedLots.length > 0 || initialLotIds.length > 0) {
        const idsToKeep = normalizedLots.filter((lot) => lot.id).map((lot) => lot.id as string);
        const idsToDelete = initialLotIds.filter((id) => !idsToKeep.includes(id));

        if (idsToDelete.length > 0) {
          const { error: deleteError } = await api
            .from('material_lots')
            .update({ is_active: false })
            .in('id', idsToDelete);
          if (deleteError) throw deleteError;
        }

        for (const lot of normalizedLots) {
          const lotPayload = {
            material_id: materialId,
            batch_number: lot.batch_number || 'SIN-LOTE',
            roll_number: lot.roll_number || null,
            quantity: Number(lot.quantity || 0),
            square_meters: Number(lot.square_meters || lot.quantity || 0),
            production_date: lot.production_date || null,
            expiry_date: lot.expiry_date || null,
            notes: lot.notes || null,
            is_active: lot.is_active,
            is_in_use: lot.is_in_use,
              removed_from_freezer_at: lot.removed_from_freezer_at || null,
              is_currently_out: lot.is_currently_out || false,
              thaw_count: lot.thaw_count || 0,
          };

          if (lot.id) {
            const { error: updateLotError } = await api
              .from('material_lots')
              .update(lotPayload)
              .eq('id', lot.id);
            if (updateLotError) throw updateLotError;
          } else {
            const { error: insertLotError } = await api
              .from('material_lots')
              .insert([lotPayload]);
            if (insertLotError) throw insertLotError;
          }
        }
      }

      toast({
        title: material ? 'Material actualizado' : 'Material creado',
        description: material
          ? 'El material y sus lotes se actualizaron correctamente'
          : 'El material y sus lotes se crearon correctamente',
      });

      queryClient.invalidateQueries({ queryKey: ['material_inventory'] });
      queryClient.invalidateQueries({ queryKey: ['material-inventory'] });
      onOpenChange(false);
      form.reset();
      setLots([]);
      setInitialLotIds([]);
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el material',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{material ? 'Editar Material' : 'Nuevo Material'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            onKeyDown={(e) => {
              // Los lectores de código de barras terminan con Enter: sin este
              // guard, el Enter dispara el submit nativo y cierra el diálogo.
              // Se guarda solo con el botón (Enter en textarea sigue funcionando).
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
                e.preventDefault();
              }
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Nombre del material" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Código</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input {...field} placeholder="Ej: MAT-001" />
                      </FormControl>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setScannerOpen(true)}
                        title="Escanear código de barras"
                      >
                        <ScanBarcode className="h-4 w-4" />
                      </Button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Hidden barcode input for USB scanner */}
            <BarcodeInput
              onScan={handleUsbScan}
              hidden
              autoFocus={false}
            />

            {/* Missing fields alert */}
            {missingFields.length > 0 && (
              <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
                <AlertCircle className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-amber-700 dark:text-amber-400">
                  <strong>Campos pendientes:</strong>
                  <ul className="list-disc list-inside mt-1">
                    {missingFields.map((field, i) => (
                      <li key={i}>{field}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoría</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar categoría" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {materialCategories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="material_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Material</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Tipo específico" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="is_freezer_material"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Material de congeladores</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Activa campos adicionales para bobinas o rollos
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Campo de tiempo máximo fuera del congelador */}
            {isFreezerMaterial && (
              <FormField
                control={form.control}
                name="max_out_time_minutes"
                render={({ field }) => (
                  <FormItem className="rounded-lg border p-4 bg-blue-50/50 dark:bg-blue-950/20">
                    <FormLabel className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Tiempo máximo fuera del congelador
                    </FormLabel>
                    <div className="flex items-center gap-3">
                      <FormControl>
                        <Input
                          type="number"
                          min={5}
                          max={99999}
                          className="w-24"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 120)}
                        />
                      </FormControl>
                      <span className="text-sm text-muted-foreground">minutos</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {[30, 60, 90, 120, 180, 240].map((mins) => (
                        <Button
                          key={mins}
                          type="button"
                          variant={field.value === mins ? "default" : "outline"}
                          size="sm"
                          onClick={() => field.onChange(mins)}
                        >
                          {mins >= 60 ? `${mins / 60}h` : `${mins}m`}
                        </Button>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Sección de Lotes - disponible para todos los materiales */}
            <div className="rounded-lg border p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">Lotes y Rollos</div>
                  <div className="text-xs text-muted-foreground">
                    {isFreezerMaterial
                      ? "Un material puede tener múltiples lotes y rollos. Marca uno o varios como 'en uso' para corte."
                      : "Registra lotes para llevar control de cada partida o rollo del material."}
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addLot}>
                  <Plus className="w-4 h-4 mr-1" /> Agregar lote
                </Button>
              </div>

              <div className="space-y-3">
                {lots.length === 0 && (
                  <div className="text-sm text-muted-foreground border rounded-md p-3 text-center">
                    No hay lotes registrados. Puedes agregar lotes para trazabilidad.
                  </div>
                )}

                  {lots.map((lot, index) => (
                    <div key={lot.id || index} className="border rounded-md p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-xs uppercase tracking-wide text-muted-foreground">
                          Lote/Rollo {index + 1}
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeLot(index)}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                          <FormLabel>Número de lote</FormLabel>
                          <Input value={lot.batch_number} onChange={(e) => updateLot(index, 'batch_number', e.target.value)} placeholder="Ej: P25-3074" />
                        </div>
                        <div>
                          <FormLabel>Número de rollo</FormLabel>
                          <Input value={lot.roll_number} onChange={(e) => updateLot(index, 'roll_number', e.target.value)} placeholder="Ej: 10" />
                        </div>
                        <div>
                          <FormLabel>Stock (unidad)</FormLabel>
                          <Input type="number" min={0} step="0.001" value={lot.quantity} onChange={(e) => updateLot(index, 'quantity', Number(e.target.value || 0))} />
                        </div>
                        <div>
                          <FormLabel>m² del rollo</FormLabel>
                          <Input type="number" min={0} step="0.0001" value={lot.square_meters} onChange={(e) => updateLot(index, 'square_meters', Number(e.target.value || 0))} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <FormLabel>Producción</FormLabel>
                          <Input type="date" value={lot.production_date || ''} onChange={(e) => updateLot(index, 'production_date', e.target.value)} />
                        </div>
                        <div>
                          <FormLabel>Caducidad</FormLabel>
                          <Input type="date" value={lot.expiry_date || ''} onChange={(e) => updateLot(index, 'expiry_date', e.target.value)} />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                          <span className="text-sm">Rollo en uso para corte</span>
                          <Switch checked={lot.is_in_use} onCheckedChange={(v) => updateLot(index, 'is_in_use', v)} />
                        </div>
                        <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                          <span className="text-sm">Lote activo</span>
                          <Switch checked={lot.is_active} onCheckedChange={(v) => updateLot(index, 'is_active', v)} />
                        </div>
                      </div>

                      {isFreezerMaterial && (
                        <div className="flex items-center gap-2 pt-2 border-t mt-2">
                          {lot.is_currently_out ? (
                            <>
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                                <Snowflake className="w-3 h-3 mr-1" />
                                Fuera del congelador
                              </Badge>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="ml-auto text-blue-600"
                                onClick={() => {
                                  updateLot(index, 'is_currently_out', false);
                                  updateLot(index, 'removed_from_freezer_at', undefined);
                                }}
                              >
                                Guardar en Congelador
                              </Button>
                            </>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="text-blue-600"
                              onClick={() => {
                                updateLot(index, 'is_currently_out', true);
                                updateLot(index, 'removed_from_freezer_at', new Date().toISOString());
                                updateLot(index, 'thaw_count', (lot.thaw_count || 0) + 1);
                              }}
                            >
                              <Snowflake className="w-4 h-4 mr-2" />
                              Sacar del Congelador
                            </Button>
                          )}
                          {lot.thaw_count ? (
                            <Badge variant="secondary" className="text-xs ml-2">
                              {lot.thaw_count}x descongelado
                            </Badge>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ))}

                {/* Resumen de totales calculados automáticamente */}
                {lots.length > 0 && (
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
                    <div className="font-medium text-sm mb-2">Totales calculados</div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Rollos:</span>{' '}
                        <span className="font-semibold">{lots.length}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">m² Totales:</span>{' '}
                        <span className="font-semibold">
                          {lots.reduce((sum, lot) => sum + (lot.square_meters || 0), 0).toFixed(2)}
                        </span>
                      </div>
                      {canSeeCosts && (
                        <div>
                          <span className="text-muted-foreground">Valor Estimado:</span>{' '}
                          <span className="font-semibold text-primary">
                            €{(lots.reduce((sum, lot) => sum + (lot.square_meters || 0), 0) * (form.watch('cost_per_unit') || 0)).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Campos adicionales para materiales de congelador */}
            {isFreezerMaterial && (
              <div className="rounded-lg border p-4 space-y-4">
                <div className="font-medium text-sm">Almacenamiento y Seguridad</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="storage_location"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ubicación de Almacenamiento</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Ej: Cámara fría A" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="storage_temperature"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Temperatura de Almacenamiento</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Ej: -18°C a 5°C" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="storage_humidity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Humedad de Almacenamiento</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Ej: <60% RH" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="safety_data_sheet_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>URL de la Hoja de Datos de Seguridad</FormLabel>
                      <FormControl>
                        <Input {...field} type="url" placeholder="https://ejemplo.com/sds.pdf" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <FormField
                control={form.control}
                name="current_stock"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stock Actual *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : 0)}
                        step="0.001"
                        min={0}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="min_stock"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stock Mínimo *</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : 0)}
                        step="0.001"
                        min={0}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="max_stock"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stock Máximo</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                        step="0.001"
                        min={0}
                        placeholder="0.000"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="square_meters"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      m² Totales {isFreezerMaterial && lots.length > 0 && <span className="text-xs text-muted-foreground">(calculado)</span>}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        value={field.value || 0}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : 0)}
                        step="0.01"
                        min={0}
                        placeholder="0.00"
                        readOnly={isFreezerMaterial && lots.length > 0}
                        className={isFreezerMaterial && lots.length > 0 ? 'bg-muted cursor-not-allowed' : ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unidad *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="kg, m, L, etc." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {canSeeCosts && (
                <FormField
                  control={form.control}
                  name="cost_per_unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Costo por m² (€/m²)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                          step="0.01"
                          min={0}
                          placeholder="0.00"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ubicación</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Almacén B - Zona 2" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="supplier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Proveedor</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Nombre del proveedor" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="last_restock_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Última Fecha de Reabastecimiento</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Notas adicionales" rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activo</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      El material está activo en el inventario
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter className="flex justify-between sm:justify-between">
              <div>
                {material && onDelete && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      if (window.confirm('Eliminar este material? Se desactivara y dejara de aparecer en el listado.')) {
                        onDelete(material);
                        onOpenChange(false);
                      }
                    }}
                    disabled={loading}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {material ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      {/* Barcode Scanner Dialog */}
      <BarcodeScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onConfirm={handleBarcodeScan}
        filterSource="material_inventory"
        title="Escanear Material"
      />
    </Dialog>
  );
}







